/**
 * The one place that knows what a locale is and how one is chosen.
 *
 * Shared by the proxy (which reads the locale per request and rewrites to the
 * matching prerendered tree), the `[locale]` layout (which prerenders one copy
 * per entry in `LOCALES`) and the language switcher (which writes the cookie
 * the proxy reads back). Keeping the list and the cookie name here is what
 * stops those three drifting apart.
 */

export const LOCALES = ["th", "en"] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * Thai is the product's primary language and what any request without an
 * explicit preference gets — including crawlers.
 *
 * This is deliberate, and it is why the proxy does NOT negotiate from
 * `Accept-Language`. The public URLs are the same for both languages (that was
 * the whole point of the rewrite approach), so whatever a crawler is served at
 * `/` is what ends up indexed. Googlebot commonly sends `Accept-Language: en`
 * or none at all, so negotiating from that header would quietly flip the
 * indexed copy of a Thai product's site to English. English visitors are
 * detected once, in the browser, by LanguageProvider — it writes the cookie
 * below and refreshes, and every request after that is server-rendered English.
 */
export const DEFAULT_LOCALE: Locale = "th";

/**
 * Name of the cookie carrying the visitor's language.
 *
 * It keeps the old localStorage key's name so a returning visitor's preference
 * is still recognisable, but the storage had to move: localStorage cannot be
 * read from the proxy, and the proxy is now what decides which language gets
 * rendered. localStorage could only be read after hydration — which is exactly
 * the 3.36 s of LCP render delay this change exists to remove.
 */
export const LOCALE_COOKIE = "jellowducks-language";

/** A year — this is a stated preference, not a session detail. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/** Resolve a raw cookie value to a locale, falling back to the default. */
export function toLocale(value: string | undefined | null): Locale {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}
